import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { User } from "@supabase/supabase-js";

export function Footer() {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("user_type")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  const isArtist = profile?.user_type === "artist";
  return (
    <footer className="border-t border-border bg-card/50">
      <div className="container py-8 md:py-12 px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-6 md:mb-8">
          {/* Om Nordic Art Collective */}
          <div>
            <h3 className="font-semibold mb-3 md:mb-4 text-sm md:text-base">Om Nordic Art Collective</h3>
            <ul className="space-y-2 text-xs md:text-sm text-muted-foreground">
              <li>
                <Link to="/about" className="hover:text-primary transition-colors">
                  Om oss
                </Link>
              </li>
              <li>
                <Link to="/artists" className="hover:text-primary transition-colors">
                  Konstnärer
                </Link>
              </li>
            </ul>
          </div>

          {/* Hjälp & Support */}
          <div>
            <h3 className="font-semibold mb-3 md:mb-4 text-sm md:text-base">Hjälp & Support</h3>
            <ul className="space-y-2 text-xs md:text-sm text-muted-foreground">
              <li>
                <Link to="/faq" className="hover:text-primary transition-colors">
                  Vanliga frågor
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-primary transition-colors">
                  Köp- och säljvillkor
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-primary transition-colors">
                  Integritetspolicy
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="hover:text-primary transition-colors">
                  Cookiepolicy
                </Link>
              </li>
            </ul>
          </div>

          {/* För konstnärer */}
          <div>
            <h3 className="font-semibold mb-3 md:mb-4 text-sm md:text-base">För konstnärer</h3>
            <ul className="space-y-2 text-xs md:text-sm text-muted-foreground">
              <li>
                <Link to="/auth" className="hover:text-primary transition-colors">
                  Bli konstnär
                </Link>
              </li>
              {isArtist && (
                <li>
                  <Link to="/artist-dashboard" className="hover:text-primary transition-colors">
                    Konstnärspanel
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h3 className="font-semibold mb-3 md:mb-4 text-sm md:text-base">Kontakt</h3>
            <address className="not-italic text-xs md:text-sm text-muted-foreground space-y-1 md:space-y-2">
              <p>Nordic Art Collective (Andberg Group AB)</p>
              <p>Org.nr: 559550-8788</p>
              <p>support@nordicartcollective.se</p>
            </address>
          </div>
        </div>

        <Separator className="mb-6 md:mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground">
          <p className="text-center md:text-left">© {new Date().getFullYear()} Nordic Art Collective (Andberg Group AB). Alla rättigheter förbehållna.</p>
          <div className="flex gap-3 md:gap-4">
            <Link to="/terms" className="hover:text-primary transition-colors">
              Villkor
            </Link>
            <Link to="/privacy" className="hover:text-primary transition-colors">
              Integritet
            </Link>
            <Link to="/cookies" className="hover:text-primary transition-colors">
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}